// react
import { useDispatch } from 'react-redux';
import * as Sentry from '@sentry/react';

// router
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTES } from 'router/routes';

// components
import { AdminBacktestPage } from '@karehero/llama/pwa/pages/AdminBacktestPage/AdminBacktestPage';

// hooks
import { TrackingEvent, useTracking } from 'hooks/useTracking';

// store
import {
  useGetBacktestQuery,
  useListBacktestRunsQuery,
  useRequestBacktestRunMutation,
} from 'store/api/evaluation';
import { newToast } from 'store/slices/uiSlice';

// utils
import { apiErrorMessage, isInputError } from 'utils/apiErrors';

const RUN_FAILED_MESSAGE = 'The run could not start.';

export const AdminBacktest = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { track } = useTracking();
  const { backtestID = '' } = useParams();
  const {
    data: backtest,
    isLoading,
    isError,
  } = useGetBacktestQuery(backtestID, { skip: !backtestID });
  const {
    data: runs = [],
    isLoading: isLoadingRuns,
    isError: isRunsError,
  } = useListBacktestRunsQuery(backtestID, { skip: !backtestID });
  const [requestRun, { isLoading: isRunning }] =
    useRequestBacktestRunMutation();

  const handleRunAgain = async () => {
    const result = await requestRun(backtestID);
    if ('error' in result) {
      if (!isInputError(result.error)) Sentry.captureException(result.error);
      dispatch(
        newToast({
          title: apiErrorMessage(result.error, RUN_FAILED_MESSAGE),
          variant: 'error',
        }),
      );
      return;
    }
    track(TrackingEvent.BacktestRunRequested, { backtestID });
    dispatch(newToast({ title: 'Run queued', variant: 'success' }));
  };

  return (
    <AdminBacktestPage
      backtest={backtest}
      runs={runs}
      isLoading={isLoading || isLoadingRuns}
      isError={isError || isRunsError}
      isRunning={isRunning}
      onRunAgain={handleRunAgain}
      onBack={() => navigate(ROUTES.ADMIN_BACKTESTS, { state: { back: true } })}
    />
  );
};

export default AdminBacktest;
